// Upload one file to an RFQ: start → PUT straight to R2 → finalize.
//
// The PUT goes to a presigned R2 URL, not our server, so a 300 MB scan never
// passes through the app (or the edge in front of it, which caps bodies).
// XMLHttpRequest rather than fetch because fetch still has no upload progress.

import {
  finalizeRfqDocument, startRfqDocumentUpload, type RfqDocumentKind, type TokenGetter,
} from '@/lib/api';
import { rfqDocumentRejection } from '@shared/rfq-documents';

export interface UploadOptions {
  rfqPublicId: string;
  file: File;
  kind: RfqDocumentKind;
  lineItemId?: string | null;
  tokenGetter: TokenGetter;
  /** 0..1 */
  onProgress?: (fraction: number) => void;
}

function putToStorage(url: string, file: File, headers: Record<string, string>, onProgress?: (f: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`Storage refused the upload (HTTP ${xhr.status}).`)));
    // A CORS refusal surfaces here with status 0 and no detail.
    xhr.onerror = () => reject(new Error('Could not reach file storage. Check your connection; if it keeps happening, the storage upload rule (CORS) is not set.'));
    xhr.send(file);
  });
}

/** Resolves with the new document id once the server has verified the file. */
export async function uploadRfqFile(o: UploadOptions): Promise<string> {
  const refusal = rfqDocumentRejection(o.file.name, o.file.size);
  if (refusal) throw new Error(refusal);
  const start = await startRfqDocumentUpload(o.rfqPublicId, {
    fileName: o.file.name, sizeBytes: o.file.size, kind: o.kind, lineItemId: o.lineItemId ?? null,
  }, o.tokenGetter);
  await putToStorage(start.uploadUrl, o.file, start.headers, o.onProgress);
  await finalizeRfqDocument(o.rfqPublicId, start.documentId, o.tokenGetter);
  return start.documentId;
}

export function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

export function errorMessage(err: unknown): string {
  return (err as { message?: string })?.message ?? 'Something went wrong.';
}
